import { useEffect, useState } from "react";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { DefaultRouteError, NotFoundView } from "@/components/RouteFallbacks";
import { CardZoom } from "@/components/CardZoom";
import { FaqSection, faqSchema } from "@/components/PageFaq";
import { InternalLinkBlock } from "@/components/InternalLinkBlock";
import { SiloGateway, AnchorLink } from "@/components/SiloGateway";
import { ShareButton } from "@/components/ShareButton";
import { breadcrumbSchema, productSchema } from "@/lib/schema";
import { indexValue } from "@/lib/prices-shared";
import { NotForSaleBadge } from "@/components/NotForSaleBadge";
import { CardMarketPanel } from "@/components/MarketPrice";
import { AffiliateDisclosure } from "@/components/AffiliateDisclosure";
import { CardAlertButtons } from "@/components/CardAlertButtons";
import {
  OFFICIAL_CARDS,
  OFFICIAL_CARD_BY_CODE,
  RARITY_MEANINGS,
  SET_NAMES,
  isNotForSale,
  normalizeCardCode,
  promoNote,
  type OfficialCard,
} from "@/lib/tcg-cards";
import { PALS } from "@/lib/pals-data";
import { CardRatingBar } from "@/components/CardRating";
import { getCardRating } from "@/lib/card-ratings.functions";
import { getCardMarketMeta } from "@/lib/prices.functions";
import { resolveGraded } from "@/lib/graded-pages";


/**
 * Per-card detail page — one indexable URL per printed *base* card number.
 *
 * Duplicate-URL policy: parallels (OSR/SSP/SR/SP/TSR/TSP) share the base card's
 * rules text, so every variant code and every non-canonical spelling
 * (lowercase, missing dash, missing leading E) redirects to the single base URL
 * instead of creating a near-duplicate page. All printings are listed on that
 * one page, so nothing is lost from the index.
 */

const BASE_BY_CODE = new Map<string, OfficialCard>();
for (const c of OFFICIAL_CARDS) if (!c.variant) BASE_BY_CODE.set(c.code.toUpperCase(), c);

/** Canonical base code for any typed/linked code, or undefined when unknown. */
function resolveBase(raw: string): OfficialCard | undefined {
  const code = normalizeCardCode(raw);
  const hit = OFFICIAL_CARD_BY_CODE.get(code);
  if (!hit) return undefined;
  return BASE_BY_CODE.get(hit.base.toUpperCase()) ?? hit;
}

export function cardPath(code: string) {
  return `/cards/${code.toUpperCase()}`;
}

function printingsOf(base: OfficialCard) {
  return OFFICIAL_CARDS.filter((c) => c.base.toUpperCase() === base.code.toUpperCase());
}

function palSlugFor(name: string) {
  return PALS.find((p) => p.name.toLowerCase() === name.toLowerCase())?.slug;
}

function cardFaq(c: OfficialCard, printings: OfficialCard[]) {
  const setName = SET_NAMES[c.set] ?? c.set;
  const parallels = printings.filter((p) => p.variant);
  return [
    {
      q: `What set is ${c.name} ${c.code} from in the Palworld card game?`,
      a: `${c.code} ${c.name} is printed in ${setName} of the Palworld OFFICIAL CARD GAME. The card number ${c.code} is printed in the lower band of the card face.`,
    },
    {
      q: `How rare is ${c.code} ${c.name}?`,
      a: `The base printing of ${c.code} is ${c.rarity}. ${RARITY_MEANINGS[c.rarity]}${
        parallels.length
          ? ` It also exists as ${parallels.length} parallel printing${parallels.length > 1 ? "s" : ""}: ${parallels
              .map((p) => `${p.code} (${p.rarity})`)
              .join(", ")}.`
          : ""
      }`,
    },
    {
      q: `What are ${c.name}'s stats on ${c.code}?`,
      a:
        c.type === "Pal"
          ? `${c.name} ${c.code} is a ${c.color} ${c.type} card with cost ◇${c.cost ?? 0}, Power ${c.power ?? 0} and ${c.strike ?? 0} strike${
              (c.strike ?? 0) === 1 ? "" : "s"
            }.`
          : `${c.code} ${c.name} is a ${c.color} ${c.type} card${c.cost != null ? ` with cost ◇${c.cost}` : ""}, so it has no Power or strike value.`,
    },
    {
      q: `What does ${c.code} ${c.name} do?`,
      a: c.effect
        ? `Printed rules text: ${c.effect.replace(/\s+/g, " ").trim()}`
        : `${c.code} ${c.name} has no printed ability text; it is used as a resource or accessory card.`,
    },
    {
      q: `How many copies of ${c.name} can I play in a Palworld TCG deck?`,
      a: `Up to 4 copies of a card name in a 50-card main deck. Parallel printings such as ${
        parallels[0]?.code ?? `${c.code}SR`
      } count towards the same 4-copy limit because they share the card name and number.`,
    },
  ];
}

export const Route = createFileRoute("/cards/$code")({
  // `?printing=` selects which printing of the card the page opens on. It never
  // creates a second indexable URL: the canonical below always points at the
  // bare base URL.
  validateSearch: (search: Record<string, unknown>): { printing?: string } =>
    typeof search.printing === "string" && search.printing ? { printing: search.printing.toUpperCase() } : {},
  // Unknown codes redirect to the card database (307) rather than soft-404,
  // and every non-canonical spelling redirects to the base card URL — keeping
  // the printing that was asked for so a parallel link lands on that parallel.
  beforeLoad: ({ params, search }) => {
    const base = resolveBase(params.code);
    if (!base) throw redirect({ to: "/cards", replace: true });
    if (base.code.toUpperCase() !== params.code.toUpperCase()) {
      const asked = normalizeCardCode(params.code).toUpperCase();
      throw redirect({
        to: "/cards/$code",
        params: { code: base.code.toUpperCase() },
        search: asked === base.code.toUpperCase() && !search.printing ? {} : { printing: search.printing ?? asked },
        replace: true,
        // Permanent: a parallel code will never own its own page, so Google must
        // consolidate the signal instead of keeping the variant URL queued.
        statusCode: 301,
      });
    }
  },

  loader: async ({ params }) => {
    const card = resolveBase(params.code)!;
    // Public read (no session required) so the server-rendered stars and the
    // aggregateRating structured data always show the same real vote counts.
    let rating = { votes: 0, average: 0 };
    try {
      const r = await getCardRating({ data: { code: card.code } });
      rating = { votes: r.votes, average: r.average };
    } catch {
      // Ratings are optional decoration for the card page.
    }
    // Live market snippet (price, 7-day move, real last-sync time and the
    // one-line reason the market moved) so the SERP description is the freshest
    // result for this card number.
    let market: Awaited<ReturnType<typeof getCardMarketMeta>> = {
      market: null,
      change7d: null,
      graded: null,
      updated: null,
      note: null,
    };
    try {
      market = await getCardMarketMeta({ data: { code: card.code } });
    } catch {
      // Market data is optional; the page still renders card data.
    }
    return { card, printings: printingsOf(card), rating, market };
  },

  head: ({ loaderData }) => {
    if (!loaderData)
      return { meta: [{ title: "Card unavailable — Pal Stars" }, { name: "robots", content: "noindex" }] };
    const { card: c, printings, rating, market } = loaderData;
    const setName = SET_NAMES[c.set] ?? c.set;
    const title = `${c.code} ${c.name} — Palworld TCG Card`;
    const stat =
      c.type === "Pal"
        ? `${c.color} ${c.rarity} Pal, cost ◇${c.cost ?? 0}, Power ${c.power ?? 0}`
        : `${c.color} ${c.rarity} ${c.type} card`;
    // Freshness-first description: price, direction and data age lead, card
    // attributes fill whatever space is left inside the 158-char budget.
    const money = (n: number) => `$${n.toFixed(2)}`;
    const pct = (n: number) => `${n > 0 ? "+" : ""}${n.toFixed(1)}%`;
    const stamp = market.updated
      ? new Date(market.updated).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" })
      : null;
    const live =
      market.market != null
        ? `${money(market.market)}${market.change7d != null ? ` (${pct(market.change7d)} 7d)` : ""}${
            stamp ? `, synced ${stamp} UTC` : ""
          }. `
        : "";
    const desc = `${c.code} ${c.name} Palworld card price ${live}${stat}, ${setName}. Rules text, rarity, parallels and graded values.`.slice(
      0,
      158,
    );
    const url = `https://palstars.com${cardPath(c.code)}`;
    return {
      meta: [
        { title: `${title} | Pal Stars` },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
        ...(c.image
          ? [
              { property: "og:image", content: c.image },
              { name: "twitter:image", content: c.image },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        faqSchema(cardFaq(c, printings), url),
        // Product markup mirrors what the page renders: the Index baseline
        // price shown in the market panel, and the printings listed below it.
        productSchema({
          name: `${c.code} ${c.name} — Palworld TCG card`,
          description: desc,
          path: cardPath(c.code),
          ...(c.image ? { image: c.image } : {}),
          sku: c.code,
          ...(rating.votes > 0 ? { rating } : {}),
          category: "Trading card game single",
          lowPrice: Math.min(...printings.map((p) => indexValue(p))),
          highPrice: Math.max(...printings.map((p) => indexValue(p))),
          offerCount: printings.length,
          properties: [
            { name: "Set", value: setName },
            { name: "Card number", value: c.code },
            { name: "Rarity", value: c.rarity },
            { name: "Color", value: c.color },
            { name: "Card type", value: c.type },
            ...(c.cost != null ? [{ name: "Cost", value: c.cost }] : []),
            ...(c.power != null ? [{ name: "Power", value: c.power }] : []),
          ],
        }),
        breadcrumbSchema([
          ["Home", "/"],
          ["Card Database", "/cards"],
          [`${c.code} ${c.name}`, cardPath(c.code)],
        ]),
      ],
    };
  },
  component: CardDetail,
  errorComponent: DefaultRouteError,
  notFoundComponent: () => (
    <NotFoundView
      title="That card number isn't in the database"
      message="No Palworld OFFICIAL CARD GAME card matches this number. Browse the card database to find the printed code."
    />
  ),
});

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-border/50 py-2 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-semibold">{value}</span>
    </div>
  );
}

function CardDetail() {
  const { card: c, printings, rating, market } = Route.useLoaderData();
  const { printing } = Route.useSearch();
  // A link from the price index / dashboard carries the exact printing that was
  // clicked (e.g. ETD01-024TSR), so the page opens on that parallel's scan and
  // market data instead of silently showing the base card.
  const [selectedCode, setSelectedCode] = useState<string | undefined>(printing);
  useEffect(() => setSelectedCode(printing), [printing]);
  const shown =
    printings.find((p) => p.code.toUpperCase() === (selectedCode ?? "").toUpperCase()) ?? c;
  const setName = SET_NAMES[c.set] ?? c.set;
  const palSlug = palSlugFor(c.name);
  const faqs = cardFaq(c, printings);
  const sameColor = OFFICIAL_CARDS.filter(
    (o) => !o.variant && o.color === c.color && o.code !== c.code && !!o.image,
  ).slice(0, 8);

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
      {/* Visible breadcrumb trail comes from the site layout (src/components/Breadcrumbs.tsx). */}


      <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
        <h1 className="text-3xl sm:text-4xl font-bold">
          {shown.code} {shown.name} — Palworld TCG Card
        </h1>
        <ShareButton
          title={`${shown.code} ${shown.name} — Palworld TCG Card`}
          text={`${shown.code} ${shown.name} Palworld TCG card price, rarity and printings.`}
          path={`/cards/${c.code.toUpperCase()}`}
        />
      </div>
      <p className="aeo-answer mt-2 max-w-3xl text-muted-foreground">
        {shown.code} {shown.name}
        {shown.subtitle ? ` “${shown.subtitle}”` : ""} is a {shown.color} {shown.rarity} {shown.type} card from {setName}
        {shown.type === "Pal"
          ? ` with cost ◇${shown.cost ?? 0}, Power ${shown.power ?? 0} and ${shown.strike ?? 0} strike`
          : ""}
        .{shown.variant ? ` It is the ${shown.rarity} parallel printing of ${c.code}.` : ""}
        {isNotForSale(shown) ? ` It is a prototype/promo printing marked Not For Sale — ${promoNote(shown)}` : ""}
      </p>
      {palSlug && <SiloGateway mode="tcg" name={c.name} target={palSlug} className="mt-4" />}
      <CardRatingBar code={c.code} initial={rating} className="mt-3" />
      {printings.some((p) => resolveGraded(p.code)) && (
        <p className="mt-3 text-sm">
          Certified copies:{" "}
          {printings
            .filter((p) => resolveGraded(p.code))
            .map((p) => (
              <Link
                key={p.code}
                to="/graded-card-prices/$code"
                params={{ code: p.code.toUpperCase() }}
                className="mr-3 text-primary hover:underline"
              >
                {p.code} PSA & BGS prices
              </Link>
            ))}
        </p>
      )}
      {(market.market != null || market.note) && (
        <p className="aeo-answer mt-3 max-w-3xl rounded-xl border border-border/60 bg-card/40 px-4 py-3 text-sm text-muted-foreground">
          {market.market != null && (
            <>
              <span className="font-semibold text-foreground">
                Market value ${market.market.toFixed(2)}
                {market.change7d != null
                  ? ` (${market.change7d > 0 ? "+" : ""}${market.change7d.toFixed(1)}% over 7 days)`
                  : ""}
                {market.graded != null ? ` · graded ${`$${market.graded.toFixed(2)}`}` : ""}
              </span>{" "}
            </>
          )}
          {market.updated && (
            <>
              Last synced from the marketplace feed{" "}
              {new Date(market.updated).toLocaleString("en-US", { timeZone: "UTC", dateStyle: "medium", timeStyle: "short" })}{" "}
              UTC.{" "}
            </>
          )}
          {market.note}
        </p>
      )}
      {isNotForSale(shown) && (
        <p className="mt-2">
          <NotForSaleBadge note={promoNote(shown)} />
        </p>
      )}


      <div className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,380px)_1fr]">
        <div>
          {c.image ? (
            <CardZoom
              src={shown.image || c.image}
              alt={`${shown.name} Palworld TCG card ${shown.code}`}
              caption={`${shown.code} — ${shown.rarity}${shown.variant ? " parallel" : " base printing"} · ${setName}`}
              cardCode={shown.code}
              cardName={c.name}
            />
          ) : (
            <div className="glass rounded-2xl p-6 text-sm text-muted-foreground">
              No official scan is published for this card yet.
            </div>
          )}
        </div>

        <div>
          <section className="glass rounded-2xl p-6">
            <h2 className="text-xl font-bold">Card data</h2>
            <div className="mt-3">
              <Row label="Card number" value={shown.code} />
              {shown.variant && <Row label="Base card number" value={c.code} />}
              <Row label="Set" value={setName} />
              <Row label="Card type" value={shown.type} />
              <Row label="Color" value={shown.color} />
              <Row label="Rarity" value={`${shown.rarity} — ${RARITY_MEANINGS[shown.rarity]}`} />
              <Row label="Printing" value={shown.variant ? `${shown.rarity} parallel` : "Base printing"} />
              {shown.cost != null && <Row label="Cost" value={`◇${shown.cost}`} />}
              {shown.power != null && <Row label="Power" value={String(shown.power)} />}
              {shown.strike != null && <Row label="Strike" value={String(shown.strike)} />}
              {shown.elements.length > 0 && <Row label="Element" value={shown.elements.join(" / ")} />}
              {shown.work.length > 0 && <Row label="Work suitability" value={shown.work.join(", ")} />}
              <Row label="Lucky Pal" value={shown.lucky ? "Yes" : "No"} />
            </div>
          </section>


          <section className="glass rounded-2xl p-6 mt-6">
            <h2 className="text-xl font-bold">Printed rules text</h2>
            <p className="aeo-answer mt-2 whitespace-pre-line text-sm leading-relaxed text-muted-foreground">
              {c.effect || "This card has no printed ability text."}
            </p>
          </section>

          {shown.code !== c.code && (
            <p className="mt-6 rounded-xl border border-primary/40 bg-primary/10 px-4 py-3 text-sm">
              Showing the <span className="font-mono font-semibold">{shown.code}</span> {shown.rarity} parallel printing.
              Parallels share this card's rules text but trade at their own price.{" "}
              <button
                type="button"
                onClick={() => setSelectedCode(undefined)}
                className="font-semibold text-primary hover:underline"
              >
                Show the base {c.code} printing
              </button>
            </p>
          )}
          <CardMarketPanel code={shown.code} />
          <div className="card-indexing-meta mt-3 text-sm text-muted-foreground" style={{ contentVisibility: "auto" }}>
            <span>Market Health: </span>
            <Link to="/tcg-price-index" className="text-primary underline hover:no-underline">
              Palworld TCG price index market trends
            </Link>
          </div>
          {/* FTC/EPN disclosure next to the affiliate buy / sold / graded links. */}
          <AffiliateDisclosure compact className="mt-3" />


          <section className="glass rounded-2xl p-6 mt-6">
            <h2 className="text-xl font-bold">All printings of {c.name}</h2>
            <ul className="mt-3 grid gap-2 sm:grid-cols-2">
              {printings.map((p) => {
                const active = p.code === shown.code;
                return (
                  <li key={p.code}>
                    <button
                      type="button"
                      aria-pressed={active}
                      onClick={() => setSelectedCode(p.variant ? p.code : undefined)}
                      className={`w-full rounded-lg border px-3 py-2 text-left text-sm transition ${
                        active ? "border-primary/60 bg-primary/10" : "border-border/60 bg-card/40 hover:bg-card/70"
                      }`}
                    >
                      <span className="font-mono font-semibold">{p.code}</span>{" "}
                      <span className="text-muted-foreground">
                        {p.rarity}
                        {p.variant ? " parallel" : " base"}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            <p className="mt-3 text-xs text-muted-foreground">
              Pick a printing to load its own scan and market prices. Parallel printings share this card's number, name
              and rules text, so they all live on this page and count towards the same 4-copy deck limit.
            </p>
          </section>

          <section className="glass rounded-2xl p-6 mt-6">
            <h2 className="text-xl font-bold">Where to use {c.code}</h2>
            <ul className="mt-3 space-y-2 text-sm">
              <li>
                <AnchorLink to="/deck-builder">
                  Add {c.name} to the Palworld TCG Deck Builder
                </AnchorLink>
              </li>
              <li>
                <AnchorLink to="/prices" search={{ card: shown.code.toUpperCase() }}>
                  View {shown.code} on the Palworld TCG Price Chart
                </AnchorLink>
              </li>
              <li>
                <AnchorLink to="/tcg-price-index">
                  Compare {c.code} against the Palworld TCG Price Index
                </AnchorLink>
              </li>
              <li>
                <AnchorLink to="/card-marketplace">
                  Buy {c.name} in the Palworld TCG Card Marketplace
                </AnchorLink>
              </li>
              <li>
                <AnchorLink to="/card-checker">
                  Verify {c.code} with the Palworld TCG Card Checker
                </AnchorLink>
              </li>
              {palSlug && (
                <li>
                  <AnchorLink to="/pals/$slug" params={{ slug: palSlug }}>
                    Palworld Pal Database: {c.name} — stats, spawns &amp; breeding
                  </AnchorLink>
                </li>
              )}
            </ul>
          </section>
        </div>
      </div>

      {sameColor.length > 0 && (
        <section className="glass rounded-2xl p-6 mt-8">
          <h2 className="text-xl font-bold">Other {c.color} Palworld cards</h2>
          <ul className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {sameColor.map((o) => (
              <li key={o.code}>
                <Link
                  to="/cards/$code"
                  params={{ code: o.code.toUpperCase() }}
                  className="text-sm text-primary hover:underline"
                >
                  {o.code} {o.name}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div id="card-guide">
        <FaqSection items={faqs} heading={`${c.code} ${c.name} card guide & FAQ`} />
      </div>
      <InternalLinkBlock path="/cards" />
    </div>
  );
}
